/**
 * Every URL the app links to. Paths are English for all fairs, whatever the UI
 * language.
 */

export type StandTab = 'jobs' | 'why' | 'gallery' | 'chat';

/**
 * Where a job page was opened from, for its back link: `search`, or the slug of
 * the pavilion the visitor reached the stand through, passed on from the stand's
 * own `from` so the stand's back link still leads to that pavilion.
 */
export type JobOrigin = string;

export const FROM_SEARCH = 'search';

/**
 * Router state a search result hands to the job page: the search's own query
 * string, so the job's back link returns to the same filtered results. State,
 * not URL, so a shared job link stays clean.
 */
export interface SearchReturnState {
  search: string;
}

export function searchReturnPath(state: unknown): string {
  const search =
    typeof state === 'object' &&
    state !== null &&
    'search' in state &&
    typeof state.search === 'string'
      ? state.search
      : '';

  return search === '' ? '/search' : `/search?${search}`;
}

export interface StandLinkOptions {
  tab?: StandTab;
  /** Slug of the pavilion the visitor came from, for the stand's back link. */
  from?: string;
}

export const paths = {
  home: '/',
  search: '/search',
  news: '/news',
  category: (slug: string): string => `/categories/${encodeURIComponent(slug)}`,
  exhibitor: (slug: string, { tab, from }: StandLinkOptions = {}): string => {
    const query = new URLSearchParams();

    if (tab !== undefined) {
      query.set('tab', tab);
    }

    if (from !== undefined) {
      query.set('from', from);
    }

    const search = query.toString();

    return `/exhibitors/${encodeURIComponent(slug)}${search === '' ? '' : `?${search}`}`;
  },
  /** Jobs sit under their exhibitor, so the URL names who is hiring. */
  job: (standSlug: string, id: number, from?: JobOrigin): string => {
    const search = from === undefined ? '' : new URLSearchParams({ from }).toString();

    return `/exhibitors/${encodeURIComponent(standSlug)}/jobs/${String(id)}${
      search === '' ? '' : `?${search}`
    }`;
  },
  article: (id: number): string => `/news/${String(id)}`,
} as const;
